import React, { FC } from 'react';
import { Box, Card, Typography, Button } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MiniTrendChart, { MiniTrendPoint } from 'screens/Authenticated/Dashboard/MiniTrendChart';
import { formatCompactIndianCurrency, formatNumber } from '../utils/formatters';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';

export type CategoryKey = 'A' | 'B' | 'C' | 'O';

export const CATEGORY_PORTFOLIO_CONFIG: Record<
  CategoryKey,
  { label: string; color: string; sparkline: MiniTrendPoint[] }
> = {
  A: {
    label: 'A - High Priority',
    color: '#2563EB',
    sparkline: [
      { date: '1', value: 18 },
      { date: '2', value: 22 },
      { date: '3', value: 20 },
      { date: '4', value: 26 },
      { date: '5', value: 24 },
      { date: '6', value: 30 },
      { date: '7', value: 28 },
    ],
  },
  B: {
    label: 'B - Medium Priority',
    color: '#059669',
    sparkline: [
      { date: '1', value: 10 },
      { date: '2', value: 12 },
      { date: '3', value: 11 },
      { date: '4', value: 14 },
      { date: '5', value: 13 },
      { date: '6', value: 16 },
      { date: '7', value: 15 },
    ],
  },
  C: {
    label: 'C - Low Priority',
    color: '#D97706',
    sparkline: [
      { date: '1', value: 14 },
      { date: '2', value: 12 },
      { date: '3', value: 13 },
      { date: '4', value: 10 },
      { date: '5', value: 11 },
      { date: '6', value: 9 },
      { date: '7', value: 8 },
    ],
  },
  O: {
    label: 'O - Others',
    color: '#7C3AED',
    sparkline: [
      { date: '1', value: 8 },
      { date: '2', value: 7 },
      { date: '3', value: 9 },
      { date: '4', value: 6 },
      { date: '5', value: 7 },
      { date: '6', value: 5 },
      { date: '7', value: 6 },
    ],
  },
};

interface CategoryPortfolioCardProps {
  category: CategoryKey;
  companyCount: number;
  revenue: number;
  roomNights: number;
  miceEvents: number;
  avgRpd: number;
  onViewCompanies?: () => void;
}

interface MetricCellProps {
  label: string;
  value: string;
}

const MetricCell: FC<MetricCellProps> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
      {value}
    </Typography>
  </Box>
);

const CategoryPortfolioCard: FC<CategoryPortfolioCardProps> = ({
  category,
  companyCount,
  revenue,
  roomNights,
  miceEvents,
  avgRpd,
  onViewCompanies,
}) => {
  const config = CATEGORY_PORTFOLIO_CONFIG[category];
  const currencySymbol = getCurrencySymbol();

  return (
    <Card
      sx={{
        p: 2.5,
        borderRadius: 2,
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: `${config.color}40`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: config.color, mb: 2 }}>
        {config.label}
      </Typography>

      <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.1 }}>
        {formatNumber(companyCount)}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', mb: 2, display: 'block' }}>
        Companies
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 1.5,
          mb: 1.5,
        }}>
        <MetricCell label="Potential" value={formatCompactIndianCurrency(revenue, currencySymbol)} />
        <MetricCell label="Room Nights" value={formatNumber(roomNights)} />
        <MetricCell label="MICE Events" value={formatNumber(miceEvents)} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.2fr',
          gap: 1,
          alignItems: 'end',
          mb: 2,
        }}>
        <MetricCell label="Avg RPD" value={formatNumber(Math.round(avgRpd))} />
        <Box sx={{ height: 48, minWidth: 0 }}>
          <MiniTrendChart data={config.sparkline} color={config.color} variant="sparkline" />
        </Box>
      </Box>

      {onViewCompanies && (
        <Box sx={{ mt: 'auto', pt: 0.5 }}>
          <Button
            variant="text"
            size="small"
            onClick={onViewCompanies}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none',
              color: 'primary.main',
              fontSize: '0.8125rem',
              fontWeight: 600,
              p: 0,
              minWidth: 'auto',
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
            }}>
            View Companies
          </Button>
        </Box>
      )}
    </Card>
  );
};

export default CategoryPortfolioCard;
